import { useRef, useState } from 'react';
import { AspectRatio } from '@/components/elements/AspectRatio';
import Button from '@/components/elements/Button';
import StarMuxVideo from '@/components/elements/StarMuxVideo';
import { IMuxVideoUploaderMuxAssetEntityResponse } from '@/graphql/__generated__/global/types';
import { useGsapContext } from '@/hooks/useGsapContext';
import classNames from '@/lib/classNames';
import { ScrollTrigger, gsap } from '@/lib/gsap';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { formatISO } from 'date-fns';
import { VideoJsonLd } from 'next-seo';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Icon } from '../../global/Icons';

export const VideoElement = ({ mux_asset, className }: { mux_asset: IMuxVideoUploaderMuxAssetEntityResponse; className?: string }) => {
    const playbackId = mux_asset?.data?.attributes?.playback_id;

    const thumbnail = `https://image.mux.com/${playbackId}/thumbnail.webp?time=${mux_asset?.data?.attributes?.thumbnailTime || 0}`;

    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [showButton, setShowButton] = useState(true);

    const ref = useRef<HTMLDivElement>(null);

    const route = useRouter();

    useGsapContext(() => {
        gsap.registerPlugin(ScrollTrigger);

        if (ref.current) {
            const timeline = gsap.timeline({
                scrollTrigger: {
                    id: 'block-video',
                    trigger: ref.current,
                    start: 'top bottom-=10%',
                    end: 'top bottom-=20%',
                    scrub: true,
                },
            });

            timeline.fromTo(
                ref.current,
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                }
            );

            return () => {
                timeline.kill();
            };
        }
    }, []);

    const videoHandler = (control: 'play' | 'pause' | 'reset') => {
        switch (control) {
            case 'play':
                if (videoRef.current) {
                    if (isFinished) {
                        setIsFinished(false);
                    }
                    videoRef.current.play();
                    setPlaying(true);
                    track(TRACKING_EVENT.PLAYED_VIDEO, {
                        title: mux_asset?.data?.attributes?.title ?? '',
                        videoId: mux_asset?.data?.id ?? '',
                        duration: mux_asset.data?.attributes?.duration ?? undefined,
                        page: route.asPath,
                    });
                    setTimeout(() => {
                        setShowButton(false);
                    }, 3200);
                }
                break;

            case 'pause':
                if (videoRef.current) {
                    videoRef.current.pause();
                    setPlaying(false);
                    setShowButton(true);

                    track(TRACKING_EVENT.PAUSED_VIDEO, {
                        title: mux_asset?.data?.attributes?.title ?? '',
                        videoId: mux_asset?.data?.id ?? '',
                        duration: mux_asset.data?.attributes?.duration ?? undefined,
                        page: route.asPath,
                    });
                }
                break;

            case 'reset':
                if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    videoHandler('play');
                }
                break;

            default:
                break;
        }
    };

    return (
        <>
            <section
                onMouseMove={() => {
                    setShowButton(true);
                }}
                onMouseLeave={() => {
                    // if (playing) {
                    // setTimeout(() => {
                    setShowButton(false);
                    // }, 750);
                    // }
                }}
                className={classNames(className, `flex flex-col items-center justify-center text-center w-full`)}
                ref={ref}
            >
                <div className="relative w-full h-full rounded-2xl overflow-hidden flex">
                    <AspectRatio ratio={16 / 9}>
                        <StarMuxVideo
                            className="w-full"
                            title={mux_asset.data?.attributes?.title ?? ''}
                            controls
                            streamType="on-demand"
                            controlsList="playback"
                            playbackId={playbackId}
                            ref={videoRef}
                            placeholder={thumbnail}
                            poster={thumbnail}
                            onEnded={() => {
                                playing && setIsFinished(true);
                            }}
                            metadata={{
                                video_title: mux_asset.data?.attributes?.title ?? '',
                            }}
                        />
                        <Image
                            src={thumbnail}
                            alt="thumbnail"
                            fill
                            className={playing ? 'object-cover opacity-0 pointer-events-none' : 'object-cover opacity-100'}
                            sizes={`(max-width: 1723px) 100vw, 1660px`}
                        />
                    </AspectRatio>
                </div>
                <div className="absolute flex items-center justify-center" onClick={() => (!isFinished ? (!playing ? videoHandler('play') : videoHandler('pause')) : videoHandler('reset'))}>
                    <Button className={`absolute hover:shadow-xl hover:scale-105 ${!showButton && 'duration-500 ease-out opacity-0'}`} variant="fill" colour="white" size="lg">
                        {!isFinished ? (
                            !playing ? (
                                <>
                                    <Icon id="play" aria-label="play" className="w-4 h-4 mr-2" />
                                    Watch Video
                                </>
                            ) : (
                                <>
                                    <Icon id="pause" aria-label="pause" className="w-4 h-4 mr-2" />
                                    Pause Video
                                </>
                            )
                        ) : (
                            <>
                                <Icon id="reset" aria-label="pause" className="w-4 h-4 mr-2" />
                                Restart Video
                            </>
                        )}
                    </Button>
                </div>
            </section>
            <VideoJsonLd
                keyOverride={mux_asset.data?.id ? mux_asset.data.id : ''}
                name={mux_asset.data?.attributes?.title ?? ''}
                description={mux_asset.data?.attributes?.title ?? ''}
                thumbnailUrls={thumbnail}
                contentUrl={`https://stream.mux.com/${playbackId}/medium.mp4`}
                uploadDate={mux_asset.data?.attributes?.createdAt ? formatISO(new Date(mux_asset.data.attributes.createdAt)) : formatISO(new Date())}
            />
        </>
    );
};
